import {
  ChangeEvent,
  InputHTMLAttributes,
  ReactElement,
  cloneElement,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
} from 'react';

export interface AttachmentUploaderRef {
  openFile: () => void;
}

export interface AttachmentUploaderProps {
  onFileChange: (file: File) => void;
  inputProps?: InputHTMLAttributes<HTMLInputElement>;
  clearFilesAfterChange?: boolean;
}

const AttachmentUploader = forwardRef<
  AttachmentUploaderRef,
  AttachmentUploaderProps
>((props: AttachmentUploaderProps, ref): ReactElement => {
  const { clearFilesAfterChange = false, onFileChange, inputProps } = props;
  const fileInput = useRef<HTMLInputElement>(null);

  const triggerFileInput = useCallback(() => {
    fileInput.current?.click();
  }, []);

  const removeFilesFromInput = () => {
    if (fileInput.current) {
      fileInput.current.value = '';
    }
  };

  useImperativeHandle(ref, () => {
    return {
      openFile: triggerFileInput,
    };
  });

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files;
    if (fileList?.length) {
      const files = Array.from(fileList);
      await Promise.all(files.map((f) => onFileChange(f)));

      if (clearFilesAfterChange) {
        removeFilesFromInput();
      }
    }
  };

  return (
    <>
      <div className="attachment-input-wrapper">
        <input
          type="file"
          name="attachment"
          ref={fileInput}
          onChange={handleFileChange}
          hidden={true}
          multiple={true}
          {...inputProps}
        />
      </div>
    </>
  );
});

AttachmentUploader.displayName = 'AttachmentUploader';

interface AttachmentWrapperProps {
  uploadItem: ReactElement;
  uploaderProps: AttachmentUploaderProps;
}

// This allows a passed element to open the attachment uploader
const AttachmentWrapper = (props: AttachmentWrapperProps): ReactElement => {
  const {
    uploadItem,
    uploaderProps: { clearFilesAfterChange, onFileChange, inputProps },
  } = props;

  const uploaderRef = useRef<AttachmentUploaderRef>(null);

  const openFile = useCallback(() => {
    if (uploaderRef.current) {
      uploaderRef.current.openFile();
    }
  }, []);

  return (
    <>
      <AttachmentUploader
        onFileChange={onFileChange}
        ref={uploaderRef}
        inputProps={inputProps}
        clearFilesAfterChange={clearFilesAfterChange}
      />
      {cloneElement(uploadItem, {
        onClick: () => {
          if (uploadItem.props.onClick) {
            uploadItem.props.onClick();
          }
          openFile();
        },
      })}
    </>
  );
};

export { AttachmentUploader, AttachmentWrapper };
